import React from "react";
import PropTypes from "prop-types";
import Icon from "../custom-widgets/icon";
import Button from "../custom-widgets/button";
import TextSuggestion from "./text-suggestion";

import styles from "./search-bar.module.scss";

/**
 * Result component.
 *
 * @param {Object} props - The component props
 * @param {Object} props.suggestion - The suggestion object containing id, title, and url or id and node for isBankers = true
 * @param {string} props.query - The current search query
 * @param {boolean} props.isBankers - Flag to determine if the search is for bankers. This changes the suggestion object structure.
 * @param {boolean} props.isLast - Flag to determine if this is the last result in the list
 * @param {number} props.index - The index of the suggestion in the list
 * @param {Function} props.onPopulateQueryButtonClick - Function to handle populate query button click, takes the new query (suggestion title or suggestion node name) as an argument.
 * @param {Function} props.onResultSearchButtonClick - Function to handle result search button click, takes the new query (suggestion title or suggestion node name) and suggestionId as arguments.
 *
 * @returns {JSX.Element} The rendered Result component
 */
const Result = ({
  suggestion = {
    id: "",
    title: "",
    url: ""
  }, // Alternatively, suggestion = { id: "", node: { Name: "" } } for isBankers = true
  query = "",
  isBankers = false,
  isLast = false,
  index = 0,
  onPopulateQueryButtonClick = () => {},
  onResultSearchButtonClick = (newQuery, suggestionId, ndex) => {}
}) => {
  let title = "";
  if (isBankers) {
    title = suggestion.node.Name;
  } else {
    title = suggestion.title?.replaceAll(" | WaFd Bank", "");
  }
  const suggestionText = title ?? suggestion.url;

  return (
    <div key={index} className={`d-flex flex-row ${isLast ? "mb-2" : ""}`}>
      <Button
        type="button"
        onClick={() => onPopulateQueryButtonClick(title)}
        icon={{
          name: "search",
          class: "fa-lg"
        }}
        class={`b-white ${styles.btnSuggestionExtraIcon}`}
        containerClass="w-auto"
      />
      <div className="d-flex flex-grow-1">
        <button
          className={`btn ${styles.btnSuggestion}`}
          onClick={() => onResultSearchButtonClick(suggestionText, suggestion.id, index)}
        >
          <TextSuggestion text={suggestionText} query={query} />
          <Icon name="arrow-right" class={`fa-lg float-right ${styles.btnSuggestionIcon}`} />
        </button>
      </div>
    </div>
  );
};

/**
 * ResultsContainer component.
 *
 * @param {Object} props - The component props
 * @param {string} props.query - The current search query
 * @param {Object} props.querySuggestions - The query suggestions object containing hits
 * @param {boolean} props.focused - Flag to determine if the search bar is focused
 * @param {boolean} props.isBankers - Flag to determine if the search is for bankers. This changes the querySuggestions hit object structure.
 * @param {Function} props.onPopulateQueryButtonClick - Function to handle populate query button click, takes the new query (suggestion title or suggestion node name) as an argument.
 * @param {Function} props.onResultSearchButtonClick - Function to handle result search button click, takes the new query (suggestion title or suggestion node name) and suggestionId as arguments.
 *
 * @returns {JSX.Element} The rendered ResultsContainer component
 */
const ResultsContainer = ({
  query = "",
  querySuggestions = { hits: [] },
  focused = false,
  isBankers = false,
  onPopulateQueryButtonClick = (newQuery) => {},
  onResultSearchButtonClick = (newQuery, suggestionId, index) => {}
}) => {
  const { hits } = querySuggestions;

  const shouldDisplayResults = focused && query !== "";
  const hasResults = hits.length > 0;

  return (
    <div className={`col-12 px-0 ${styles.suggestionsContainer}`}>
      {shouldDisplayResults && hasResults && (
        <div className="pl-0">
          {hits.map((suggestion, index) => (
            <Result
              key={suggestion.id}
              suggestion={suggestion}
              query={query}
              index={index}
              onPopulateQueryButtonClick={onPopulateQueryButtonClick}
              onResultSearchButtonClick={onResultSearchButtonClick}
              isLast={index === hits.length - 1}
              isBankers={isBankers}
            />
          ))}
        </div>
      )}
      {shouldDisplayResults && !hasResults && (
        <div className="input-group mb-2 mx-3">
          <p className="h6 mb-0">No results found</p>
        </div>
      )}
    </div>
  );
};

export default ResultsContainer;

Result.propTypes = {
  suggestion: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string,
    url: PropTypes.string,
    node: PropTypes.shape({
      Name: PropTypes.string
    })
  }).isRequired,
  query: PropTypes.string.isRequired,
  isBankers: PropTypes.bool.isRequired,
  isLast: PropTypes.bool.isRequired,
  index: PropTypes.number.isRequired,
  onPopulateQueryButtonClick: PropTypes.func.isRequired,
  onResultSearchButtonClick: PropTypes.func.isRequired
};

ResultsContainer.propTypes = {
  query: PropTypes.string.isRequired,
  querySuggestions: PropTypes.shape({
    hits: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        title: PropTypes.string,
        url: PropTypes.string,
        node: PropTypes.shape({
          Name: PropTypes.string
        })
      })
    ).isRequired
  }).isRequired,
  focused: PropTypes.bool.isRequired,
  isBankers: PropTypes.bool.isRequired,
  onPopulateQueryButtonClick: PropTypes.func.isRequired,
  onResultSearchButtonClick: PropTypes.func.isRequired
};
